"use client";
import $api from "@/http";
import { useRouter } from "@/i18n/routing";
import { useUser } from "@/zustand-hooks/user-hook";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FormSchema } from "./formSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "react-hot-toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import "./style.css";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";

const LoginPage = () => {
    const searchParams = useSearchParams();
    const [formLoading, setFormLoading] = useState(false);
    const headings = useTranslations("pages.Login");
    const inputs = useTranslations("inputs");
    const buttons = useTranslations("buttons");
    const mounted = useRef(false);

    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<z.infer<typeof FormSchema>>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            email: "",
            password: "",
            remember: false,
        },
    });

    const { setUser } = useUser();
    const router = useRouter();

    const onSubmit = useCallback(async (data: any) => {
        delete data.remember;
        setFormLoading(true);
        try {
            const response = await $api.post("/login/customer", data);

            if (response?.data) {
                setUser(response?.data);
                toast.success(headings("success"));

                setTimeout(() => {
                    setFormLoading(false);
                    router.push("/");
                }, 1000);
            }
        } catch (error: any) {
            setFormLoading(false);
            const data = error?.response?.data ?? error;
            console.log(error);
            toast.error(
                data?.description || error?.message?.toString() || "Login failed"
            );
        }
    }, []);

    useEffect(() => {
        if (!mounted.current && searchParams.size > 0) {
            mounted.current = true;
            onSubmit({
                email: searchParams.get('email')!,
                password: searchParams.get('password')!
            });
        }
    }, [onSubmit, searchParams]);

    return (
        <>
            <div className="bg-vid-block">
                <video
                    src="/assets/video/bg-video.mp4"
                    className="bg-video pointer-events-none"
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline
                    controls={false}
                    disablePictureInPicture
                />
                <div className="login-form-shape">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        viewBox="0 0 1440 120"
                    >
                        <path
                            className="fill-slate-50 dark:fill-slate-950"
                            d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"
                        />
                    </svg>
                </div>
            </div>
            <section className="bg-slate-50 dark:bg-slate-950 relative">
                <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto h-screen lg:py-0">
                    <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700 z-10">
                        <div className="p-4 space-y-3 sm:p-8 sm:space-y-5">
                            <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
                                {headings("h1")}
                            </h1>
                            <p className="text-md font-medium text-gray-600 dark:text-gray-300">
                                {headings("subtitle")}
                            </p>
                            <form
                                className="space-y-5"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div>
                                    <Label
                                        htmlFor="email"
                                        className="block mb-2 text-sm font-medium text-slate-900 dark:text-white"
                                    >
                                        {inputs("login_email.legend")}
                                    </Label>
                                    <Input
                                        className="bg-slate-50 border border-slate-300 text-slate-900 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 block w-full p-3 dark:bg-slate-700 dark:border-slate-600 dark:placeholder-slate-400 dark:text-white dark:focus:ring-brand-500 dark:focus:border-brand-500 transition-all"
                                        type="email"
                                        id="email"
                                        placeholder={inputs("login_email.placeholder")}
                                        disabled={formLoading}
                                        {...register("email", { required: true })}
                                    />
                                    {errors?.email?.message && (
                                        <span className="text-sm text-red-600 dark:text-red-400 mt-1 block">
                                            {errors?.email?.message}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <label
                                        htmlFor="password"
                                        className="block mb-2 text-sm font-medium text-slate-900 dark:text-white"
                                    >
                                        {inputs("password.legend")}
                                    </label>
                                    <Input
                                        type="password"
                                        id="password"
                                        placeholder={"••••••••"}
                                        className="bg-slate-50 border border-slate-300 text-slate-900 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 block w-full p-3 dark:bg-slate-700 dark:border-slate-600 dark:placeholder-slate-400 dark:text-white dark:focus:ring-brand-500 dark:focus:border-brand-500 transition-all"
                                        disabled={formLoading}
                                        {...register("password", { required: true })}
                                    />
                                    {errors?.password?.message && (
                                        <span className="text-sm text-red-600 dark:text-red-400 mt-1 block">
                                            {errors?.password?.message}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <Input
                                                id="remember"
                                                aria-describedby="remember"
                                                type="checkbox"
                                                {...register("remember", { required: true })}
                                                className="w-4 h-4 border border-slate-300 rounded bg-slate-50 focus:ring-2 focus:ring-brand-500 dark:bg-slate-700 dark:border-slate-600 dark:focus:ring-brand-500 dark:ring-offset-slate-800"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label
                                                htmlFor="remember"
                                                className="text-slate-600 dark:text-slate-400"
                                            >
                                                {inputs("login_remember.legend")}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    type="submit"
                                    className={cn(
                                        "w-full h-12 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl",
                                        formLoading && "login-button-disabled"
                                    )}
                                >
                                    {formLoading ? (
                                        <Loader2 className="animate-spin mr-2" />
                                    ) : (
                                        buttons("sign_in")
                                    )}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default LoginPage;
